import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Donut } from 'app/components/Common/Donut/Donut';
import { RetentionBarChart, IRetentionApiItem } from './RetentionBarChart';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(6),
    backgroundColor: 'white',
    padding: theme.spacing(3, 0),
    width: '100%',
    minHeight: 320,
  },
  donutWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 32%',
    minWidth: 220,
    alignSelf: 'stretch',
  },
  barChartWrapper: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
}));

const COLORS = {
  booked: '#1C6BC3',
  rebooked: '#379ADF',
};

export interface IRetentionChartsProps {
  items: IRetentionApiItem[];
  fyStartYear: number;
  visualsOn?: boolean;
  selectedBarChartMonth?: string | null;
  onBarChartMonthClick?: (month: string) => void;
}

export const RetentionCharts: React.FC<IRetentionChartsProps> = ({
  items,
  fyStartYear,
  visualsOn = true,
  selectedBarChartMonth = null,
  onBarChartMonthClick,
}) => {
  const { classes } = useStyles();

  const donutData = React.useMemo(() => {
    let booked = 0;
    let rebooked = 0;
    items.forEach(item => {
      if (String(item.rebooked ?? '').toUpperCase() === 'Y') rebooked++;
      else booked++;
    });
    return { booked, rebooked };
  }, [items]);

  const donutFiltered = [
    { name: 'Rebooked', value: donutData.rebooked },
    { name: 'Booked', value: donutData.booked },
  ].filter(d => d.value > 0);

  if (!visualsOn) return null;

  return (
    <Box className={classes.root}>
      <Box className={classes.donutWrapper}>
        <Donut
          data={donutFiltered.length > 0 ? donutFiltered : [{ name: 'placeholder', value: 0 }]}
          colors={[COLORS.rebooked, COLORS.booked]}
          labelTitle="CANDIDATES"
          displayValue={String(donutData.booked + donutData.rebooked)}
          labelNoWrap={true}
          hasLegend={false}
          noRounding={true}
        />
      </Box>
      <Box className={classes.barChartWrapper}>
        <RetentionBarChart
          items={items}
          fyStartYear={fyStartYear}
          selectedMonth={selectedBarChartMonth}
          onMonthClick={onBarChartMonthClick}
        />
      </Box>
    </Box>
  );
};
